// CE-04g-UI-indicators-live-tick — pure helpers shared by the live-tick
// data pump (Effect 3 in src/components/chart/TradeChart.jsx).
//
// The reconcile split introduced in this slice routes the trailing bar
// of each indicator's compute through `series.update()` on a 200ms
// throttle, instead of the prior `setData()` full reconcile that only
// fired on settled-bar boundaries. To do that the pump needs an
// `effectiveBars` array that includes the forming (in-flight) bar — and
// the forming bar must compose OHLC from the per-tick `barStateRef` with
// volume from the ~5s `liveCandlesRef[last]` refetch (the per-tick
// stream carries no volume; only the forming-bar correction does).
//
// These two helpers are pure so they can be unit-tested in isolation.
// They never touch refs, the chart, or React.

/**
 * Build a single forming-bar OHLC+volume snapshot from the in-memory tick
 * state plus the latest live-candles entry.
 *
 * Returns null when the tick state has not yet learned a bar time or
 * landed a valid close. The pump treats null as "no work to do" for this
 * cycle.
 *
 * Volume comes from `liveCandles[last]` ONLY when its time matches the
 * forming bar's time AND the volume is finite. Otherwise volume is 0 —
 * indicator computes that read `volume` (Volume bars; VWAP via OHLC4 ×
 * volume) treat 0 as "no contribution this bar" rather than crashing on
 * NaN/undefined. The CE-04g-UI-vwap-window helpers already coerce
 * `Number(c.volume) || 0` for the same reason.
 *
 * @param {{ time: number | null, rawEpochMs?: number | null, open: number, high: number, low: number, close: number } | null} barState
 * @param {Array<{ time: number, volume?: number }> | null | undefined} liveCandles
 * @returns {{ time: number, rawEpochMs: number | null, open: number, high: number, low: number, close: number, volume: number } | null}
 */
export function synthesizeFormingBar(barState, liveCandles) {
  if (barState == null) return null
  if (barState.time == null) return null
  if (!Number.isFinite(barState.close)) return null

  let volume = 0
  if (Array.isArray(liveCandles) && liveCandles.length > 0) {
    const last = liveCandles[liveCandles.length - 1]
    if (last && last.time === barState.time && Number.isFinite(last.volume)) {
      volume = last.volume
    }
  }

  return {
    time: barState.time,
    rawEpochMs: barState.rawEpochMs ?? null,
    open: barState.open,
    high: barState.high,
    low: barState.low,
    close: barState.close,
    volume,
  }
}

/**
 * Compose the bars array fed to the indicator compute layer during a
 * live-pump tick. Settled bars come from `displayCandles`; the forming
 * bar replaces or appends the trailing point depending on time alignment.
 *
 * - `formingBar == null` → return `displayCandles` (same reference, no
 *   allocation in the hot path).
 * - empty `displayCandles` OR `formingBar.time > last.time` → append.
 * - `formingBar.time === last.time` → replace last.
 * - `formingBar.time < last.time` (defensive — race between a delayed
 *   tick and a settled-bar boundary that's already moved `displayCandles`
 *   forward) → return `displayCandles` unchanged.
 *
 * Never mutates the input arrays.
 *
 * @param {Array<{time: number}>} displayCandles
 * @param {{time: number} | null} formingBar
 * @returns {Array<{time: number}>}
 */
export function composeEffectiveBars(displayCandles, formingBar) {
  if (formingBar == null) return displayCandles
  if (!Array.isArray(displayCandles) || displayCandles.length === 0) {
    return [formingBar]
  }
  const lastTime = displayCandles[displayCandles.length - 1].time
  if (formingBar.time > lastTime) {
    return [...displayCandles, formingBar]
  }
  if (formingBar.time === lastTime) {
    const out = displayCandles.slice(0, displayCandles.length - 1)
    out.push(formingBar)
    return out
  }
  return displayCandles
}

/**
 * Reconcile a runtime candle into the client-owned live tail.
 *
 * The initial HTTP snapshot and SSE stream describe the same bar sequence.
 * An SSE forming update may only replace that sequence's trailing bar or
 * append a newer one. A completed update may also replace an already-known
 * bar because IBKR can deliver its final checkpoint after the next bar opens.
 * We deliberately never invent a gap or rewrite an unknown historic bar.
 */
export function reconcileRuntimeCandle(bars, incoming, { completed = false } = {}) {
  if (!incoming || !Number.isFinite(incoming.time)) {
    return { accepted: false, bars: Array.isArray(bars) ? bars : [] }
  }
  const current = Array.isArray(bars) ? bars : []
  if (current.length === 0) return { accepted: true, bars: [incoming] }

  const lastIndex = current.length - 1
  const last = current[lastIndex]
  const index = current.findIndex((bar) => bar?.time === incoming.time)
  if (index >= 0) {
    if (!completed && index !== lastIndex) {
      return { accepted: false, bars: current }
    }
    const next = current.slice()
    next[index] = { ...next[index], ...incoming }
    return { accepted: true, bars: next, index }
  }
  if (incoming.time > last.time) {
    return { accepted: true, bars: [...current, incoming], index: current.length }
  }
  return { accepted: false, bars: current }
}
